/**
 * Minimal x-reference walker for the EIDOS framework (EID-47).
 *
 * Walks a node subtree against its JSON Schema in lockstep, collecting
 * every `x-reference`-annotated value. Used by `derive.ts` to rewrite
 * internal cross-references when a source subtree is cloned for a
 * derived node.
 *
 * This is a Solid-agnostic plain JS module — it duplicates a subset of
 * the platform-side walker (`eidos-platform/app/src/utils/schema/
 * referenceWalker.ts`) on purpose: the platform is React and ships in a
 * separate npm app, so cross-runtime sharing isn't free. Promoting both
 * implementations to a shared package is a follow-up (noted in the
 * EID-47 spec).
 */

export type JsonSchema = Record<string, unknown>;

export type RefKind = 'data' | 'layer' | 'panel' | 'node';

export interface RefSite {
  /** Dot/bracket path of the value within the subtree root. */
  path: string;
  /** The actual ID string at this path. */
  value: string;
  /** Reference kind from the schema's `x-reference` annotation. */
  kind: RefKind;
  /** Reference scope from the schema's `x-reference` annotation. */
  scope: string;
}

/**
 * Walk a subtree against a schema and return every `x-reference` site.
 * Returns sites with absolute paths *within the subtree*. Duplicate sites
 * at the same path are deduped (first-emission wins).
 */
export function walkReferences(
  subtree: unknown,
  schema: JsonSchema,
  allSchemas: Record<string, JsonSchema> = {},
): RefSite[] {
  const sites: RefSite[] = [];
  walk(subtree, schema, schema, allSchemas, [], sites, new Set<string>());
  return dedupe(sites);
}

// ---------------------------------------------------------------------------
// Walk
// ---------------------------------------------------------------------------

function walk(
  data: unknown,
  schema: JsonSchema | undefined,
  rootSchema: JsonSchema,
  allSchemas: Record<string, JsonSchema>,
  path: (string | number)[],
  sites: RefSite[],
  visitedRefs: Set<string>,
): void {
  if (data == null || schema == null || typeof schema !== 'object') return;

  // Follow $ref
  if (typeof schema.$ref === 'string') {
    const ref = schema.$ref;
    if (visitedRefs.has(ref)) return;
    const resolved = resolveRef(ref, rootSchema, allSchemas);
    if (!resolved) return;
    const next = new Set(visitedRefs);
    next.add(ref);
    walk(
      data,
      resolved.schema,
      resolved.rootSchema,
      allSchemas,
      path,
      sites,
      next,
    );
    return;
  }

  // oneOf — pick the branch matching the data's discriminator (or first)
  if (Array.isArray(schema.oneOf)) {
    const branch = pickBranch(
      schema.oneOf as JsonSchema[],
      data,
      rootSchema,
      allSchemas,
    );
    if (branch) {
      walk(
        data,
        branch.schema,
        branch.rootSchema,
        allSchemas,
        path,
        sites,
        visitedRefs,
      );
    }
    return;
  }

  if (Array.isArray(schema.anyOf)) {
    for (const item of schema.anyOf as JsonSchema[]) {
      walk(data, item, rootSchema, allSchemas, path, sites, visitedRefs);
    }
    return;
  }
  if (Array.isArray(schema.allOf)) {
    for (const item of schema.allOf as JsonSchema[]) {
      walk(data, item, rootSchema, allSchemas, path, sites, visitedRefs);
    }
    // fall through to walk own properties too
  }

  // Leaf — record an x-reference site
  const xRef = schema['x-reference'] as
    | { kind: RefKind; scope?: string }
    | undefined;
  if (xRef && typeof data === 'string') {
    sites.push({
      path: pathToString(path),
      value: data,
      kind: xRef.kind,
      scope: xRef.scope ?? 'root',
    });
    return;
  }

  // Object
  if (typeof data === 'object' && !Array.isArray(data)) {
    const props = schema.properties as Record<string, JsonSchema> | undefined;
    if (!props) return;
    for (const [key, value] of Object.entries(
      data as Record<string, unknown>,
    )) {
      const propSchema = props[key];
      if (propSchema) {
        walk(
          value,
          propSchema,
          rootSchema,
          allSchemas,
          [...path, key],
          sites,
          visitedRefs,
        );
      }
    }
    return;
  }

  // Array
  if (Array.isArray(data)) {
    const items = schema.items as JsonSchema | undefined;
    if (!items) return;
    data.forEach((item, idx) => {
      walk(
        item,
        items,
        rootSchema,
        allSchemas,
        [...path, idx],
        sites,
        visitedRefs,
      );
    });
  }
}

interface ResolvedBranch {
  schema: JsonSchema;
  rootSchema: JsonSchema;
}

function pickBranch(
  branches: JsonSchema[],
  data: unknown,
  rootSchema: JsonSchema,
  allSchemas: Record<string, JsonSchema>,
): ResolvedBranch | null {
  const resolved: ResolvedBranch[] = branches.map((b) =>
    resolveBranchOnce(b, rootSchema, allSchemas),
  );
  if (typeof data === 'object' && data !== null) {
    for (const r of resolved) {
      if (matchesDiscriminator(r.schema, data)) return r;
    }
  }
  // Returning the first branch when nothing matches (the previous
  // behaviour) silently mis-walks the data against an unrelated schema,
  // which then mis-rewrites or skips refs. Better to abandon this
  // subtree — the worst that happens is some refs aren't rewritten.
  return null;
}

function resolveBranchOnce(
  branch: JsonSchema,
  rootSchema: JsonSchema,
  allSchemas: Record<string, JsonSchema>,
): ResolvedBranch {
  if (typeof branch?.$ref === 'string') {
    const r = resolveRef(branch.$ref, rootSchema, allSchemas);
    if (r) return r;
  }
  return { schema: branch, rootSchema };
}

function matchesDiscriminator(schema: JsonSchema, data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  const props = schema?.properties as Record<string, JsonSchema> | undefined;
  if (!props) return false;
  for (const key of ['nodeType', 'layerType'] as const) {
    const propSchema = props[key];
    const expected = (propSchema as JsonSchema | undefined)?.const;
    if (typeof expected === 'string') {
      return (data as Record<string, unknown>)[key] === expected;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// $ref resolution (local + external eidos refs)
// ---------------------------------------------------------------------------

interface ResolvedRef {
  schema: JsonSchema;
  rootSchema: JsonSchema;
}

function resolveRef(
  ref: string,
  rootSchema: JsonSchema,
  allSchemas: Record<string, JsonSchema>,
): ResolvedRef | null {
  // Bare `#` refers to the root document itself.
  if (ref === '#') {
    return { schema: rootSchema, rootSchema };
  }
  if (ref.startsWith('#/')) {
    const target = resolveLocalPath(ref.slice(2), rootSchema);
    return target ? { schema: target, rootSchema } : null;
  }
  const match = ref.match(
    /^https:\/\/schemas\.oceanum\.io\/eidos\/(.+?)\.json(#.*)?$/,
  );
  if (!match) return null;
  const externalSchema = allSchemas[match[1]];
  if (!externalSchema) return null;
  const fragment = match[2] ?? '';
  if (fragment.startsWith('#/')) {
    const target = resolveLocalPath(fragment.slice(2), externalSchema);
    return target ? { schema: target, rootSchema: externalSchema } : null;
  }
  return { schema: externalSchema, rootSchema: externalSchema };
}

function resolveLocalPath(path: string, root: JsonSchema): JsonSchema | null {
  const parts = path.split('/').filter(Boolean);
  let cur: unknown = root;
  for (const part of parts) {
    if (cur == null || typeof cur !== 'object') return null;
    const decoded = part.replace(/~1/g, '/').replace(/~0/g, '~');
    cur = (cur as Record<string, unknown>)[decoded];
  }
  return cur != null && typeof cur === 'object' ? (cur as JsonSchema) : null;
}

// ---------------------------------------------------------------------------
// Path / dedup helpers
// ---------------------------------------------------------------------------

function pathToString(parts: (string | number)[]): string {
  let out = '';
  for (const p of parts) {
    if (typeof p === 'number') {
      out += `[${p}]`;
    } else {
      out += out ? `.${p}` : p;
    }
  }
  return out;
}

function dedupe(sites: RefSite[]): RefSite[] {
  const byPath = new Map<string, RefSite>();
  for (const site of sites) {
    if (!byPath.has(site.path)) byPath.set(site.path, site);
  }
  return Array.from(byPath.values());
}
